<template>

    <Message :active="state.results_message_active && state.search_string.length > 0 && filtered_accounts.length === 0" heading="No accounts match your search" type="error" class="mb-2" />

    <Combobox as="div" v-model="state.selected_account" @update:modelValue="state.search_string = ''" class="min-w-[260px]">
        <ComboboxLabel v-if="label" class="block text-sm font-medium text-gray-900 mb-2">{{ label }}</ComboboxLabel>
        <div class="relative">

            <ComboboxInput
                class="w-full h-[38px] rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                v-bind:class="{ 'bg-white': !disabled, 'bg-gray-200': disabled }"
                :disabled="disabled"
                @change="state.search_string = $event.target.value"
                @blur="state.search_string = ''"
                :display-value="(account) => account?.name"
                :placeholder="placeholder || 'Account name or code'"
                autocomplete="off"
            />

            <ComboboxButton v-if="!state.selected_account" class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clip-rule="evenodd" />
                </svg>
            </ComboboxButton>

            <div v-if="enable_create" class="absolute inset-y-0 right-[40px] flex items-center">
                <button v-on:click="() => state.create_modal_active = true" class="flex grow-0 h-auto items-center justify-center border border-transparent focus:outline-none w-auto rounded-md border border-1 border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:border-blue-700 focus:ring-blue-500 focus:ring-2 focus:ring-offset-2 shadow-sm px-2 py-1 text-xs font-medium" v-if="!state.selected_account" size="sm" width="w-auto">Add new</button>
            </div>

            <button v-if="state.selected_account && !disabled" v-on:click="() => clearSelectedAccount()" class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 bg-transparent focus:outline-none">
                <CrossCircleIcon class="h-5 w-5 text-gray-700" />
            </button>

            <ComboboxOptions v-if="Array.isArray(filtered_accounts) && filtered_accounts.length" class="absolute z-10 mt-1 max-h-60 min-w-full max-w-[600px] w-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <ComboboxOption v-for="account in filtered_accounts" :key="account.id" :value="account" as="template" v-slot="{ active, selected }">
                    <li :class="['relative cursor-default select-none py-2 pl-3 pr-9 hover:cursor-pointer', active ? 'bg-blue-600 text-white' : 'text-gray-900']">
                        <span :class="['block truncate', selected && 'font-semibold']">
                            {{ account.name }}
                        </span>
                        <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-blue-600']">
                            <CheckIcon class="h-4 w-4" />
                        </span>
                    </li>
                </ComboboxOption>
                <ComboboxOption v-if="enable_create" as="template" v-slot="{ active, selected }">
                    <li class="relative cursor-default select-none py-2 pl-3 pr-9 hover:cursor-pointer">
                        <button v-on:click="() => state.create_modal_active = true" class="w-full bg-transparent block truncate font-semibold text-blue-600 text-left">
                            Add new
                        </button>
                    </li>
                </ComboboxOption>
            </ComboboxOptions>
        </div>
    </Combobox>

    <div v-if="state.selected_account && !disable_hint" class="w-full my-3 p-3 rounded-lg bg-blue-50 border bordere-1 border-blue-200 text-blue-700">
        <p class="font-medium">{{state.selected_account?.account?.name || state.selected_account.name}}</p>
        <p v-if="state.selected_account.account?.email" class="text-sm">{{ state.selected_account.account.email }}</p>
        <p v-if="state.selected_account.account?.location_postcode" class="text-sm">
            <span v-if="state.selected_account.account.location_address_line_1">{{ state.selected_account.account.location_address_line_1 }}</span> <span v-if="state.selected_account.account.location_town">{{ state.selected_account.account.location_town }}</span> <span v-if="state.selected_account.account.location_postcode">{{ state.selected_account.account.location_postcode }}</span>
        </p>
        <p v-if="state.selected_account.account?.location" class="text-sm">{{`${state.selected_account.account?.location.address_line_1}, ${state.selected_account.account?.location.town}, ${state.selected_account.account?.location.postcode}`}}</p>
        <div v-else-if="Array.isArray(state.selected_account.account?.locations) && state.selected_account.account?.locations.length">
            <p v-for="location in state.selected_account.account?.locations" :key="location.id" class="text-sm">{{`${location.address_line_1}, ${location.town}, ${location.postcode}`}}</p>
        </div>
    </div>

    <Modal
        :active="state.create_modal_active"
        size="xl"
    >
        <AccountCreate
            :auctioneer_id="auctioneer_id"
            :enable_add_another="false"
            :account_type="account_type"
            :cancelAction="closeCreateModal"
            v-on:account_created="account => newAccountCreated(account)"
        />
    </Modal>

</template>

<script>
import { reactive, computed, watch, onMounted, ref } from "vue";
import { useAccountStore } from "@sto/account.store"

import AccountCreate from '@par/account/AccountCreate.vue'
import CheckIcon from "@ico/CheckIcon.vue"
import CrossCircleIcon from "@ico/CrossCircleIcon.vue";
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue'

export default {
    components: {
        AccountCreate,
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions,
        CheckIcon,
        CrossCircleIcon
    },
    props: [
        'auctioneer_id',
        'account_type',
        'disabled',
        'label',
        'placeholder',
        'enable_create',
        'existing_account_id',
        'disable_hint'
    ],
    setup(props, {emit}) {

        const ACCOUNT_STORE = useAccountStore()

        const state = reactive({
            accounts_loading: false,
            account_options: [],
            search_string: '',
            selected_account: null,
            account_timeout: null,
            results_message_active: false,
            create_modal_active: false
        })

        const injectAndSetAccount = account => {

            if (!account) return

            let location_postcode = '',
                location_town = ''

            if (account.location_postcode) {
                location_postcode = account.location_postcode
            } else if (account.location && account.location.postcode) {
                location_postcode = account.location.postcode
            } else if (Array.isArray(account.locations) && account.locations.length) {
                location_postcode = account.location[0].postcode
            }

            if (account.location_town) {
                location_town = account.location_town
            } else if (account.location && account.location.town) {
                location_town = account.location.town
            } else if (Array.isArray(account.locations) && account.locations.length) {
                location_town = account.location[0].town
            }

            const account_option = {
                id: account?.id,
                name: `${account.name} - ${location_postcode}, ${location_town}`,
                account: account
            }

            state.selected_account = account_option
            state.account_options = [account_option]
        }

        const getAccounts = async () => {

            if (state.accounts_loading || !state.search_string) return

            state.accounts_loading = true

            try {

                let params = [
                    { key: 'per_page', value: 25 },
                    { key: 'page', value: 1 },
                    { key: 'search_string', value: state.search_string },
                ]

                switch (props.account_type) {
                    case 'buyers':
                        params.push({ key: 'has_buyer', value: 1 })
                        break
                    case 'sellers':
                        params.push({ key: 'has_seller', value: 1 })
                        break
                }

                if (props.auctioneer_id) {
                    params.push({ key: 'auctioneer_id', value: props.auctioneer_id })
                }

                const accounts_data = await ACCOUNT_STORE.find(params)
                // const accounts_data = await ACCOUNT_STORE.list(params)

                if (!Array.isArray(accounts_data?.accounts)) return

                state.account_options = accounts_data.accounts.map((acc) => {
                    return {
                        id: acc.id,
                        name: `${acc.name} (${acc.location_postcode}, ${acc.location_town})`,
                        account: acc
                    }
                })

                state.results_message_active = true

            } catch (error) {
                console.error('error', error)

            } finally {
                state.accounts_loading = false
            }
        }

        const filtered_accounts = computed(() => {
            return state.search_string === ''
                ? state.account_options
                : state.account_options.filter((account) => {
                    return account.name.toLowerCase().includes(state.search_string.toLowerCase())
                })
        })

        const clearSelectedAccount = () => {
            state.selected_account = null
            state.account_options = []
        }

        const newAccountCreated = account => {
            state.create_modal_active = false
            state.selected_account = {
                id: account?.id || null,
                name: account?.name || null,
                account: account
            }
            emit('account_selected', account)
        }

        const closeCreateModal = () => {
            state.create_modal_active = false
        }

        const getExistingAccount = async account_id => {
            try {
                state.accounts_loading = true
                const account = await ACCOUNT_STORE.show(account_id)

                injectAndSetAccount(account)
            } catch (error) {
                console.error('ACCOUNT_STORE.show error', error)
            } finally {
                state.accounts_loading = false
            }
        }

        const newAccountSelected = async account_id => {
            if (!account_id) {
                emit('account_selected', null)
                return
            }
            const account = await ACCOUNT_STORE.show(account_id)
            emit('account_selected', account || null)
        }

        watch(
            () => state.search_string,
            (new_value) => {
                state.results_message_active = false
                if (state.account_timeout) clearTimeout(state.account_timeout)
                state.account_timeout = setTimeout(getAccounts, 500)
            }
        )

        watch(
            () => state.selected_account,
            (new_value) => {

                // emit('account_selected', state.selected_account?.account || null)
                newAccountSelected(state.selected_account?.account?.id)
            }
        )

        watch (
            () => props.auctioneer_id,
            () => {
                getAccounts()
            }
        )

        watch (
            () => props.existing_account_id,
            () => {
                if (props.existing_account_id != state.selected_account?.id) getExistingAccount(props.existing_account_id)
            }
        )

        onMounted(() => {
            getAccounts()
            if (props.existing_account_id) getExistingAccount(props.existing_account_id)
        })

        return {
            state,
            filtered_accounts,
            clearSelectedAccount,
            newAccountCreated,
            closeCreateModal,
            injectAndSetAccount
        }
    }
}
</script>